import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import Animated, { Easing, interpolate, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { ds } from '@/components/lib/design.system';
import { common, designSystem } from '@/styles/styles';
import { Outcome, Result, VoidType } from '@/types/api.generated';
import {
    PICK_STATUS_CONFIG,
    PickStatus,
    getPickStatus,
    isValueOutOfSyncWithResult,
    shouldFillBar,
} from '@/utils/pick-status';

import { LoadingSkeleton } from './SkeletonLoader';
import { Text } from './TextComponent';
import { Box } from './lib/components/Box';

export type ProgressBarProps = {
    max: number;
    animated: boolean;
    isLiveSportWithoutTracking: boolean;
    value?: number | null;
    result?: Result;
    outcome: Outcome;
    loading: boolean;
    voidType?: VoidType;
};

const END_BAR_WIDTH = 24;

const TRACKING_UNAVAILABLE_SEPARATOR = {
    color: 'neutral300',
    testID: 'tracking-unavailable-separator-text',
    i18nKey: 'pickem_entries:tracking_unavailable',
} as const;

export const Progressbar = memo(
    ({
        value = 0,
        animated,
        max,
        result,
        outcome,
        loading,
        voidType,
        isLiveSportWithoutTracking,
    }: ProgressBarProps) => {
        const { t } = useTranslation(['pickem_entries', 'common']);
        const [barContainerWidth, setBarContainerWidth] = useState(0);
        const [textWidth, setTextWidth] = useState(0);
        const progressValue = useSharedValue(0);
        const status = getPickStatus({ result, voidType });
        const { barColor, hasColoredFill, separator: separatorLabel } = PICK_STATUS_CONFIG[status];

        // the score can lag behind settlement:
        // a value that contradicts the result is treated as one that has not arrived yet, so the bar keeps loading
        const hasStaleValue =
            !isLiveSportWithoutTracking && isValueOutOfSyncWithResult({ result, outcome, value, max });

        const hasValue = value != null && !hasStaleValue;
        const isLoaded = !loading && !hasStaleValue;

        const displayCurrentValue = hasValue && status !== PickStatus.VOID && !isLiveSportWithoutTracking;
        const displaySeparator = (isLoaded && !shouldFillBar(result, outcome, voidType)) || isLiveSportWithoutTracking;
        const isEndBarFilled = shouldFillBar(result, outcome) && !isLiveSportWithoutTracking;

        const fillBarBackgroundColor = hasColoredFill ? barColor : ds.colorValue.background.neutral.inverse;
        const endBarBackgroundColor = isEndBarFilled ? barColor : ds.colorValue.background.neutral.strong;

        useEffect(() => {
            if (animated) {
                progressValue.value = withTiming(1, { duration: 500, easing: Easing.inOut(Easing.ease) });
            } else {
                progressValue.value = 1;
            }
        }, [value, progressValue, animated]);

        const separator = useMemo(() => {
            const label = isLiveSportWithoutTracking ? TRACKING_UNAVAILABLE_SEPARATOR : separatorLabel;

            if (!label) {
                return <Box style={styles.lineSeparator} testID={'line-separator-text'} />;
            }

            return (
                <Text
                    color={label.color}
                    variant={'bodySmall'}
                    letterSpacing={-0.08}
                    style={styles.textSpace}
                    testID={label.testID}
                >
                    {t(label.i18nKey)}
                </Text>
            );
        }, [isLiveSportWithoutTracking, separatorLabel, t]);

        const getBarWidth = useCallback((event: LayoutChangeEvent) => {
            setBarContainerWidth(event.nativeEvent.layout.width);
        }, []);

        const getTextWidth = useCallback((event: LayoutChangeEvent) => {
            const gap = 6;
            setTextWidth(event.nativeEvent.layout.width + gap);
        }, []);

        const leadBarAnimatedStyle = useAnimatedStyle(() => {
            const valueProgress = Math.min((value ?? 0) / max, 1);
            const leadBarWidth = valueProgress * (barContainerWidth - textWidth);
            return {
                width: interpolate(progressValue.value, [0, 0.9], [0, leadBarWidth], 'clamp'),
            };
        });

        const endBarAnimatedStyle = useAnimatedStyle(() => {
            return {
                width: interpolate(progressValue.value, [0, 0.9, 1], [0, 0, END_BAR_WIDTH]),
            };
        });

        return (
            <View style={styles.root}>
                <View style={styles.progressBar} onLayout={getBarWidth}>
                    {displayCurrentValue ? (
                        <>
                            {/** the leading part of the progress bar */}
                            <Animated.View
                                style={[
                                    styles.content,
                                    {
                                        backgroundColor: fillBarBackgroundColor,
                                    },
                                    leadBarAnimatedStyle,
                                ]}
                            />
                            {/** the value overlayed on top of the progress bar */}
                            <View onLayout={getTextWidth} style={value > 0 ? styles.textSpace : styles.textPadding}>
                                <Text color={ds.colorToken.text.primary} variant={'bodySmall'} letterSpacing={-0.08}>
                                    {value}
                                </Text>
                            </View>
                        </>
                    ) : null}
                    <View style={common.flex}>
                        {isLoaded ? (
                            <View style={styles.content} />
                        ) : (
                            <LoadingSkeleton
                                style={styles.content}
                                height={4}
                                animationSpeed={1500}
                                skeletonColor={designSystem.colors.neutral400}
                                skeletonGradient={[
                                    'transparent',
                                    designSystem.colors.neutral400,
                                    designSystem.colors.neutral400,
                                    'transparent',
                                ]}
                                positions={[0, 0.25, 0.75, 1]}
                            />
                        )}
                    </View>
                </View>

                {displaySeparator ? separator : null}
                {isLoaded ? (
                    <View style={[styles.content, { width: END_BAR_WIDTH }]}>
                        {/** end part of the progress bar */}
                        <Animated.View
                            style={[
                                styles.content,
                                {
                                    backgroundColor: endBarBackgroundColor,
                                },
                                endBarAnimatedStyle,
                            ]}
                        />
                    </View>
                ) : null}
            </View>
        );
    }
);

const styles = StyleSheet.create({
    root: {
        borderRadius: 4,
        borderCurve: 'continuous',
        alignItems: 'center',
        flexDirection: 'row',
        width: '100%',
    },
    content: {
        borderRadius: 2,
        borderCurve: 'continuous',
        height: 4,
        backgroundColor: ds.colorValue.background.neutral.strong,
    },
    lineSeparator: {
        height: 14,
        width: 2,
        borderRadius: 2,
        borderCurve: 'continuous',
        marginHorizontal: 6,
        backgroundColor: ds.colorValue.icon.tertiary,
    },
    progressBar: { flex: 1, flexDirection: 'row', alignItems: 'center' },
    textSpace: { marginLeft: 6, paddingRight: 6 },
    textPadding: { paddingRight: 6 },
});
